import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  BluetoothRounded as BluetoothIcon,
  BluetoothConnectedRounded as ConnectedIcon,
  PrintRounded as PrintIcon,
  LinkOffRounded as ForgetIcon,
  ExpandMoreRounded as ExpandIcon,
} from "@mui/icons-material";
import {
  PRINTER_LANGUAGES,
  btSupport,
  forgetBtPrinter,
  getBtState,
  loadBtSettings,
  pickBtPrinter,
  reconnectBtPrinter,
  saveBtSettings,
  subscribeBtPrinter,
} from "./btPrinter";
import {
  LABEL_MEDIA_KEY,
  MEDIA_PRESETS,
  DEFAULT_LABEL_MM,
  mediaKey,
  mediaLabel,
  readLabelPref,
  writeLabelPref,
} from "../barcodeMapper/labelSvg";
import { BLUE, BORDER, INK, INK_MUTED, SUCCESS } from "./shared";

// ─── Bluetooth printer setup ─────────────────────────────────────────────────
// One screen for the whole local-printing story on this device: pair a printer,
// say what it speaks and what stock it holds, prove it with a test label, and
// turn on printing-on-save.
//
// Everything here is per-device (localStorage), because that is what it
// describes — the same teller signs in at a counter with a SATO on the bridge
// and on a phone with a pocket printer in their bag, and the two must not share
// a setting.

// Widths offered beside "auto". These are print-head sizes, not paper sizes: a
// 58 mm receipt printer images 384 dots (48 mm) and wraps anything wider, which
// is the single most common reason a first test label comes out in two ragged
// halves.
const WIDTH_OPTIONS = [
  { value: "auto", label: "Auto — from the label size" },
  { value: 384, label: "384 dots — 58 mm printer" },
  { value: 576, label: "576 dots — 80 mm printer" },
  { value: 320, label: "320 dots — 40 mm" },
  { value: 400, label: "400 dots — 50 mm" },
  { value: 464, label: "464 dots — 58 mm label" },
  { value: 832, label: "832 dots — 104 mm" },
];

const BluetoothPrinterDialog = ({ open, onClose, onTestPrint }) => {
  const support = btSupport();
  const [cfg, setCfg] = useState(loadBtSettings);
  const [bt, setBt] = useState(getBtState);
  const [media, setMedia] = useState(() =>
    readLabelPref(LABEL_MEDIA_KEY, mediaKey(DEFAULT_LABEL_MM)),
  );
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => subscribeBtPrinter(setBt), []);

  // Re-read on open: the printer may have been paired from another screen, and
  // the connection state is owned by the module, not by this component.
  useEffect(() => {
    if (!open) return;
    setCfg(loadBtSettings());
    setMedia(readLabelPref(LABEL_MEDIA_KEY, mediaKey(DEFAULT_LABEL_MM)));
    setError("");
    setNote("");
  }, [open]);

  const patch = (next) => setCfg(saveBtSettings(next));

  const run = async (key, fn) => {
    setBusy(key);
    setError("");
    setNote("");
    try {
      await fn();
    } catch (err) {
      setError(err?.message || "Something went wrong");
    } finally {
      setBusy("");
    }
  };

  const handleConnect = () =>
    run("connect", async () => {
      // Reconnecting to an already-paired printer is silent; only a first
      // pairing opens the chooser. Trying the quiet path first means the
      // operator is not asked to pick their printer out of a list every time.
      if (cfg.deviceId && (await reconnectBtPrinter())) {
        setCfg(loadBtSettings());
        return;
      }
      setCfg(await pickBtPrinter());
    });

  const handleTest = () =>
    run("test", async () => {
      await onTestPrint();
      // Three distinct symptoms, three different settings. Worth naming all of
      // them here: they are indistinguishable as "the label came out wrong".
      setNote(
        "Test label sent. Command text instead of a label → wrong printer " +
          "language. Wrapped or split into ragged halves → pin the print width " +
          "to your printer's head. Upside down → tick Rotate 180°. Nothing at " +
          "all → check paper, then try 20 bytes per write under Advanced.",
      );
    });

  const connected = bt.connected;
  const paired = !!cfg.deviceId;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      PaperProps={{ sx: { borderRadius: 3, width: "100%", maxWidth: 460 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: "1rem",
          fontWeight: 700,
          color: INK,
          pb: 1,
        }}
      >
        <BluetoothIcon sx={{ color: BLUE }} />
        Bluetooth Label Printer
      </DialogTitle>

      <DialogContent>
        {!support.supported ? (
          <Alert severity="warning" sx={{ fontSize: "0.8rem" }}>
            {support.reason}
          </Alert>
        ) : (
          <>
            {/* ── Pairing ───────────────────────────────────────────────── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                p: 1.5,
                borderRadius: 2,
                border: `1.5px solid ${connected ? SUCCESS : BORDER}`,
                bgcolor: connected ? "#ECFDF5" : "#F8FAFC",
              }}
            >
              {busy === "connect" ? (
                <CircularProgress size={24} sx={{ color: BLUE }} />
              ) : (
                <ConnectedIcon
                  sx={{ color: connected ? SUCCESS : INK_MUTED, fontSize: 26 }}
                />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{ fontSize: "0.85rem", fontWeight: 700, color: INK }}
                  noWrap
                >
                  {cfg.deviceName || "No printer paired"}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED }}>
                  {connected
                    ? "Connected — labels print straight to this printer"
                    : paired
                      ? "Paired, not connected"
                      : "Pair the printer once; it reconnects on its own after that"}
                </Typography>
              </Box>
              <Button
                size="small"
                variant={connected ? "outlined" : "contained"}
                disabled={!!busy}
                onClick={handleConnect}
                sx={{ flex: "0 0 auto" }}
              >
                {connected ? "Reconnect" : paired ? "Connect" : "Pair"}
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 1.5, fontSize: "0.78rem" }}>
                {error}
              </Alert>
            )}
            {note && (
              <Alert severity="info" sx={{ mt: 1.5, fontSize: "0.78rem" }}>
                {note}
              </Alert>
            )}

            {/* ── Auto-print ────────────────────────────────────────────── */}
            <FormControlLabel
              sx={{ mt: 1.5, ml: 0 }}
              control={
                <Switch
                  checked={!!cfg.enabled}
                  onChange={(e) => patch({ enabled: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography
                    sx={{ fontSize: "0.85rem", fontWeight: 700, color: INK }}
                  >
                    Print the barcode label on every save
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED }}>
                    On this device only. While it is on, the label is not sent to
                    the counter's printer.
                  </Typography>
                </Box>
              }
            />

            <Divider sx={{ my: 1.75, borderColor: BORDER }} />

            {/* ── What the printer is ───────────────────────────────────── */}
            <Stack spacing={1.75}>
              <FormControl fullWidth size="small">
                <InputLabel>Printer language</InputLabel>
                <Select
                  label="Printer language"
                  value={cfg.language}
                  onChange={(e) => patch({ language: e.target.value })}
                >
                  {PRINTER_LANGUAGES.map((l) => (
                    <MenuItem
                      key={l.id}
                      value={l.id}
                      sx={{ fontSize: "0.82rem" }}
                    >
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
                {/* The one symptom that identifies a wrong language, spelled
                    out: the printer prints the commands. Nothing about a page
                    of "^XA^LH0,0" suggests "change the dropdown above" unless
                    someone says so — the natural reading is a broken label. */}
                <Typography
                  sx={{
                    mt: 0.75,
                    fontSize: "0.72rem",
                    color: INK_MUTED,
                    lineHeight: 1.5,
                  }}
                >
                  Not sure? Send a test label. If the printer spits out lines of
                  text like <b>^XA^LH0,0</b> or <b>SIZE 50 mm</b>, the language
                  is wrong — try the next one. A real label, even a badly placed
                  one, means the language is right.
                </Typography>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Label stock</InputLabel>
                <Select
                  label="Label stock"
                  value={media}
                  onChange={(e) => {
                    setMedia(e.target.value);
                    writeLabelPref(LABEL_MEDIA_KEY, e.target.value);
                  }}
                >
                  {MEDIA_PRESETS.map((m) => (
                    <MenuItem key={mediaKey(m)} value={mediaKey(m)}>
                      {mediaLabel(m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Print width</InputLabel>
                <Select
                  label="Print width"
                  value={cfg.widthDots}
                  onChange={(e) => patch({ widthDots: e.target.value })}
                >
                  {WIDTH_OPTIONS.map((o) => (
                    <MenuItem
                      key={o.value}
                      value={o.value}
                      sx={{ fontSize: "0.82rem" }}
                    >
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1.5}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Copies</InputLabel>
                  <Select
                    label="Copies"
                    value={cfg.copies}
                    onChange={(e) => patch({ copies: e.target.value })}
                  >
                    {[1, 2, 3].map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Resolution</InputLabel>
                  <Select
                    label="Resolution"
                    value={cfg.dpi}
                    onChange={(e) => patch({ dpi: e.target.value })}
                  >
                    <MenuItem value={203}>203 dpi (8 dots/mm)</MenuItem>
                    <MenuItem value={300}>300 dpi (12 dots/mm)</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Switch
                    checked={!!cfg.rotate180}
                    onChange={(e) => patch({ rotate180: e.target.checked })}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.82rem", color: INK }}>
                    Rotate 180°
                  </Typography>
                }
              />
            </Stack>

            {/* ── Advanced ──────────────────────────────────────────────── */}
            <Button
              size="small"
              endIcon={
                <ExpandIcon
                  sx={{
                    transform: advanced ? "rotate(180deg)" : "none",
                    transition: "transform .15s",
                  }}
                />
              }
              onClick={() => setAdvanced((v) => !v)}
              sx={{ mt: 1, color: INK_MUTED, fontSize: "0.78rem" }}
            >
              Advanced
            </Button>

            {advanced && (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {cfg.language === "escpos" && (
                  <TextField
                    size="small"
                    type="number"
                    label="Feed lines after the label"
                    value={cfg.feedLines}
                    onChange={(e) => patch({ feedLines: e.target.value })}
                    helperText="Enough to clear the tear bar."
                  />
                )}
                {cfg.language === "tspl" && (
                  <TextField
                    size="small"
                    type="number"
                    label="Gap between labels (mm)"
                    value={cfg.gapMm}
                    onChange={(e) => patch({ gapMm: e.target.value })}
                    helperText="Must match the roll, or the printer ejects blanks hunting for the gap."
                  />
                )}
                <TextField
                  size="small"
                  type="number"
                  label="Bytes per Bluetooth write"
                  value={cfg.chunkBytes}
                  onChange={(e) => patch({ chunkBytes: e.target.value })}
                  helperText="Drop to 20 if labels print only partly — some modules drop long writes."
                />
                <TextField
                  size="small"
                  label="Printer service UUID (optional)"
                  value={cfg.service}
                  onChange={(e) => patch({ service: e.target.value })}
                  helperText="Only needed if the printer pairs but no print channel is found. Re-pair after setting it."
                />
                {paired && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<ForgetIcon />}
                    disabled={!!busy}
                    onClick={() =>
                      run("forget", async () => setCfg(await forgetBtPrinter()))
                    }
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Forget this printer
                  </Button>
                )}
              </Stack>
            )}

            <Typography
              sx={{ mt: 2, fontSize: "0.72rem", color: INK_MUTED, lineHeight: 1.5 }}
            >
              Only Bluetooth <b>LE</b> printers can be reached from a browser. A
              Classic/SPP-only printer will not appear in the pairing list even
              though Android shows it as paired.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {support.supported && (
          <Button
            variant="outlined"
            startIcon={
              busy === "test" ? (
                <CircularProgress size={16} sx={{ color: BLUE }} />
              ) : (
                <PrintIcon />
              )
            }
            disabled={!!busy || (!connected && !paired)}
            onClick={handleTest}
            sx={{ mr: "auto" }}
          >
            Test label
          </Button>
        )}
        <Button onClick={onClose} disabled={!!busy} sx={{ color: INK_MUTED }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BluetoothPrinterDialog;
